import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, Brain, Heart, Moon, Mic, 
  Apple, Phone, Wind, Palette, Package,
  Filter, Save, Zap, Info, Car, Music, 
  Utensils, Tv, Headphones, Dumbbell,
  ArrowRight, DollarSign, TrendingUp, 
  Clock, Sparkles, MessageCircle, History
} from 'lucide-react';

interface SignalType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  baseValue: number;
  unit: string;
}

interface RealWorldOutput {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  signalCost: number;
  category: 'Food' | 'Tech' | 'Services' | 'Art';
  tooltip: string;
}

interface SignalHistory {
  id: string;
  date: string;
  amount: number;
  type: string;
  description: string;
}

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
}

const signalTypes: SignalType[] = [
  {
    id: 'movement',
    name: 'Movement',
    icon: <Activity className="w-6 h-6" />,
    description: 'Physical activity and motion data',
    baseValue: 1000,
    unit: 'KB'
  },
  {
    id: 'focus',
    name: 'Focus',
    icon: <Brain className="w-6 h-6" />,
    description: 'Attention and concentration patterns',
    baseValue: 2000,
    unit: 'KB'
  },
  {
    id: 'emotion',
    name: 'Emotion',
    icon: <Heart className="w-6 h-6" />,
    description: 'Emotional resonance and patterns',
    baseValue: 3000,
    unit: 'KB'
  },
  {
    id: 'dreams',
    name: 'Dreams',
    icon: <Moon className="w-6 h-6" />,
    description: 'Sleep and dream state data',
    baseValue: 4000,
    unit: 'KB'
  },
  {
    id: 'voice',
    name: 'Voice/Thought Logs',
    icon: <Mic className="w-6 h-6" />,
    description: 'Voice recordings and thought patterns',
    baseValue: 5000,
    unit: 'KB'
  }
];

const realWorldOutputs: RealWorldOutput[] = [
  {
    id: 'apple',
    name: 'Organic Apple',
    icon: <Apple className="w-6 h-6" />,
    description: 'Fresh, organic apple from local farmers',
    signalCost: 1000,
    category: 'Food',
    tooltip: 'This organic apple usually costs $2 — but in the SELFverse, it costs 1,000 SU of movement data.'
  },
  {
    id: 'phone-service',
    name: 'SELF Phone Service',
    icon: <Phone className="w-6 h-6" />,
    description: '1 Week of SELF Phone service',
    signalCost: 5000,
    category: 'Tech',
    tooltip: 'A week of SELF Phone service usually costs $10 — but in the SELFverse, it costs 5,000 SU of focus data.'
  },
  {
    id: 'breathwork',
    name: 'DAO Breathwork Session',
    icon: <Wind className="w-6 h-6" />,
    description: 'Guided breathwork session with the DAO',
    signalCost: 3000,
    category: 'Services',
    tooltip: 'A DAO breathwork session usually costs $15 — but in the SELFverse, it costs 3,000 SU of emotional data.'
  },
  {
    id: 'ai-art',
    name: 'AI-Generated Art Print',
    icon: <Palette className="w-6 h-6" />,
    description: 'Unique AI-generated artwork print',
    signalCost: 4000,
    category: 'Art',
    tooltip: 'An AI art print usually costs $20 — but in the SELFverse, it costs 4,000 SU of dream data.'
  },
  {
    id: 'self-store',
    name: 'SELF Store Product',
    icon: <Package className="w-6 h-6" />,
    description: 'Choose from available SELF Store items',
    signalCost: 2000,
    category: 'Tech',
    tooltip: 'SELF Store products usually cost $5-50 — but in the SELFverse, they cost 2,000-20,000 SU of voice data.'
  }
];

const realWorldExamples = [
  {
    id: 'rideshare',
    name: 'Rideshare',
    icon: <Car className="w-6 h-6" />,
    description: 'You spend ~$200/month on Uber or Lyft.',
    signalValue: "This month's movement data + location signal = 4 Essence-covered rides.",
    category: 'Transportation'
  },
  {
    id: 'music',
    name: 'Spotify / Apple Music',
    icon: <Music className="w-6 h-6" />,
    description: 'Your playlists and mood patterns = 1 month premium subscription.',
    signalValue: 'Paid in Essence. No credit card needed.',
    category: 'Entertainment'
  },
  {
    id: 'food',
    name: 'Dinner Delivery',
    icon: <Utensils className="w-6 h-6" />,
    description: 'Your daily rhythm + biometric data = a DoorDash gift card.',
    signalValue: 'Redeemed with signal.',
    category: 'Food'
  },
  {
    id: 'phone',
    name: 'SELF Phone Bill',
    icon: <Phone className="w-6 h-6" />,
    description: 'Covered by your movement, journaling, and AI usage signal.',
    signalValue: 'Presence = payment.',
    category: 'Tech'
  },
  {
    id: 'streaming',
    name: 'Netflix / Hulu',
    icon: <Tv className="w-6 h-6" />,
    description: 'Your attention powered entertainment. Now it powers your plan.',
    signalValue: 'Streaming paid in signal.',
    category: 'Entertainment'
  },
  {
    id: 'meditation',
    name: 'Headspace / Calm',
    icon: <Headphones className="w-6 h-6" />,
    description: 'You meditated. That peace created value — and paid it forward.',
    signalValue: 'Mindfulness = membership.',
    category: 'Wellness'
  },
  {
    id: 'gym',
    name: 'Gym Membership',
    icon: <Dumbbell className="w-6 h-6" />,
    description: 'Movement logs from your smartwatch = access to your next class.',
    signalValue: 'Your activity = your access.',
    category: 'Fitness'
  }
];

const testimonials: Testimonial[] = [
  {
    id: '1',
    quote: "I used to pay $15/month for meditation apps. Now my mindfulness practice pays for itself.",
    author: "Sarah Chen",
    role: "Yoga Instructor"
  },
  {
    id: '2',
    quote: "My daily walks now cover my DoorDash orders. It's like getting paid to exercise!",
    author: "Marcus Rodriguez",
    role: "Remote Worker"
  },
  {
    id: '3',
    quote: "The SELF Phone pays for itself through my normal usage. It's revolutionary.",
    author: "Aisha Patel",
    role: "Tech Entrepreneur"
  }
];

const signalHistory: SignalHistory[] = [
  {
    id: '1',
    date: 'Today',
    amount: 1350,
    type: 'movement',
    description: 'Your morning walk earned 1,350 SU'
  },
  {
    id: '2',
    date: 'Yesterday',
    amount: 2800,
    type: 'focus',
    description: 'Your journal entry helped train CalmAI 3.0'
  },
  {
    id: '3',
    date: '2 days ago',
    amount: 950,
    type: 'emotion',
    description: 'Your emotional data improved our wellness algorithms'
  }
];

export default function SignalValueCalculator() {
  const [selectedSignals, setSelectedSignals] = useState<Record<string, number>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showFiatValue, setShowFiatValue] = useState<boolean>(false);
  const [currentTestimonial, setCurrentTestimonial] = useState<number>(0);
  const [signalRarity, setSignalRarity] = useState<number>(70);
  const [weeklySignals, setWeeklySignals] = useState<Record<string, number>>({
    movement: 2500,
    focus: 1800,
    emotion: 1200,
    dreams: 800,
    voice: 950
  });

  const totalSignalUnits = Object.entries(selectedSignals).reduce((total, [type, amount]) => {
    const signalType = signalTypes.find(s => s.id === type);
    return total + (signalType ? amount * signalType.baseValue : 0);
  }, 0);

  const weeklyTotal = Object.entries(weeklySignals).reduce((total, [type, amount]) => {
    const signalType = signalTypes.find(s => s.id === type);
    return total + (signalType ? amount * signalType.baseValue : 0);
  }, 0);

  const minSignalValue = weeklyTotal * 0.01;
  const maxSignalValue = weeklyTotal * 0.03;
  const essenceValue = Math.round(weeklyTotal * 1.5);

  const topSignalTypes = Object.entries(weeklySignals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([type]) => type);

  const getRecommendations = () => {
    const recommendations = [];
    if (weeklySignals.movement > 2000) {
      recommendations.push({
        id: 'gym',
        name: 'Gym Membership',
        icon: <Dumbbell className="w-5 h-5" />,
        description: 'Your high movement data makes you eligible for fitness rewards'
      });
    }
    if (weeklySignals.focus > 1500) {
      recommendations.push({
        id: 'streaming',
        name: 'Streaming Services',
        icon: <Tv className="w-5 h-5" />,
        description: 'Your focus patterns qualify you for entertainment rewards'
      });
    }
    if (weeklySignals.emotion > 1000) {
      recommendations.push({
        id: 'meditation',
        name: 'Meditation Apps',
        icon: <Headphones className="w-5 h-5" />,
        description: 'Your emotional data makes you eligible for wellness rewards'
      });
    }
    return recommendations;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSignalChange = (type: string, value: number) => {
    setSelectedSignals(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const getRemainingSignal = (cost: number) => {
    const remaining = cost - totalSignalUnits;
    return remaining > 0 ? remaining : 0;
  };

  const getRemainingMessage = (output: RealWorldOutput) => {
    const remaining = getRemainingSignal(output.signalCost);
    if (remaining === 0) return "You have enough signal!";
    
    const signalType = output.id.includes('phone') ? 'focus' : 
                      output.id.includes('breathwork') ? 'emotion' :
                      output.id.includes('art') ? 'dreams' :
                      output.id.includes('apple') ? 'movement' : 'voice';
    
    const dailyRate = weeklySignals[signalType] / 7;
    const daysNeeded = Math.ceil(remaining / (dailyRate * signalTypes.find(s => s.id === signalType)?.baseValue || 1));
    
    return `${daysNeeded} more ${daysNeeded === 1 ? 'day' : 'days'} of ${signalType} data and it's yours.`;
  };

  const filteredOutputs = realWorldOutputs.filter(output => 
    selectedCategory === 'all' || output.category === selectedCategory
  );

  const unlockedOutputs = filteredOutputs.filter(output => 
    totalSignalUnits >= output.signalCost
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-[#080010] text-white py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🧬 Welcome to the Signal Economy
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            You're already working.<br />
            Now you're finally getting paid.
          </p>
        </motion.div>

        {/* What Is This Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🧠 What Is This?
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg">
              Every day, you generate something more valuable than money:<br />
              <span className="text-indigo-400 font-semibold">Your presence. Your focus. Your movement. Your signal.</span>
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">When you:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Commute using Google Maps or Uber</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Listen to playlists on Spotify or Apple Music</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Scroll TikTok, Instagram, or YouTube Shorts</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Track steps on your phone or smartwatch</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Talk to Siri or Alexa</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Join Zoom calls or leave voice notes</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Order food from DoorDash or Instacart</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Binge shows on Netflix or Hulu</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Meditate with Headspace or Calm</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Google something random</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>Ask ChatGPT to help you out...</span>
                </div>
              </div>
            </div>
            
            <p className="text-lg">
              You're producing rich, high-value signal — every single time.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-xl font-semibold text-indigo-400">
                That signal trains AI systems.<br />
                It powers billion-dollar platforms.<br />
                And you've never seen a penny of it.
              </p>
            </div>
            
            <p className="text-lg">
              SELF Labs flips the system.
            </p>
            
            <p className="text-lg">
              We created <span className="text-indigo-400 font-semibold">Essence</span> — the first currency you earn simply by being alive.<br />
              And you can spend it on the things you already use every day: Music. Rides. Food. Your phone bill. Subscriptions. Wellness.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-xl font-semibold text-indigo-400">
                You're not the product anymore.<br />
                You're the producer.<br />
                And now, you get paid.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Why Data Can Replace Money Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            💡 Why Data Can Replace Money
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg">
              Money gets you what you need: a ride, a playlist, a phone plan.
            </p>
            
            <p className="text-lg">
              But your data is already doing the same thing — just not for you.
            </p>
            
            <p className="text-lg">
              Every click, step, and moment of attention creates signal that AI companies buy.<br />
              They pay the platforms — not the people.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-xl font-semibold text-indigo-400">
                Essence changes that.
              </p>
            </div>
            
            <p className="text-lg">
              Now, your signal becomes your spending power.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <p className="text-gray-300">
                  $9.99 for Spotify? →<br />
                  <span className="text-indigo-400">Your mood signal can cover it.</span>
                </p>
              </div>
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <p className="text-gray-300">
                  $200/month on Uber? →<br />
                  <span className="text-indigo-400">Your movement signal earns the ride.</span>
                </p>
              </div>
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <p className="text-gray-300">
                  Phone bill? →<br />
                  <span className="text-indigo-400">Your attention and focus already paid for it.</span>
                </p>
              </div>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-xl font-semibold text-indigo-400">
                You've already paid — with your life.<br />
                Essence lets you redeem it.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Why a Single Kilobyte Can Be Priceless Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            📈 Why a Kilobyte Can Be Priceless
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg">
              And why more signal unlocks even more power.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">🧪 A single kilobyte (KB) of signal can:</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-4">Signal Type</th>
                      <th className="text-left py-4 px-4">What It Trains</th>
                      <th className="text-left py-4 px-4">Example Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Mood check-in</td>
                      <td className="py-4 px-4">Emotional intent classifier</td>
                      <td className="py-4 px-4">Helps Spotify recommend songs that match your mood</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">GPS ping</td>
                      <td className="py-4 px-4">Human route prediction</td>
                      <td className="py-4 px-4">Trains Uber/Lyft models to adjust for real-world delays</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Search query</td>
                      <td className="py-4 px-4">Intent modeling</td>
                      <td className="py-4 px-4">Helps Google refine result quality and answer accuracy</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Voice tone snippet</td>
                      <td className="py-4 px-4">Sentiment analysis</td>
                      <td className="py-4 px-4">Helps Siri/Alexa understand when you're frustrated</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4">Journal tag</td>
                      <td className="py-4 px-4">Daily reflection patterns</td>
                      <td className="py-4 px-4">Trains mindfulness AIs to detect mental states</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-lg mt-4">
                🧠 Even a few KB from your day can help AIs feel more human.
              </p>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">📦 A megabyte (MB) of signal can:</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-4">Signal Type</th>
                      <th className="text-left py-4 px-4">What It Trains</th>
                      <th className="text-left py-4 px-4">Example Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Voice recording (2–5 mins)</td>
                      <td className="py-4 px-4">Conversational agents + customer support bots</td>
                      <td className="py-4 px-4">Trains AI to understand pacing, tone, hesitation</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Biometric + step data (1 day)</td>
                      <td className="py-4 px-4">Health + activity models</td>
                      <td className="py-4 px-4">Trains wearables to adjust based on fatigue</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Focus logs (1–3 sessions)</td>
                      <td className="py-4 px-4">Productivity models, tutoring AI</td>
                      <td className="py-4 px-4">Helps AI suggest breaks, optimize time</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Streaming reaction logs</td>
                      <td className="py-4 px-4">Emotion-based recommendations</td>
                      <td className="py-4 px-4">Improves YouTube/Netflix recommendation systems</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4">Combined browsing + intent logs</td>
                      <td className="py-4 px-4">Ad engines + predictive search</td>
                      <td className="py-4 px-4">Powers intelligent suggestion tools</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-lg mt-4">
                ⚙️ Just 1 MB of your signal can help train models used by millions daily.
              </p>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">💾 A gigabyte (GB) of signal can:</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-4">Signal Type</th>
                      <th className="text-left py-4 px-4">What It Trains</th>
                      <th className="text-left py-4 px-4">Example Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Full-day multi-modal log (movement, focus, mood, voice)</td>
                      <td className="py-4 px-4">Next-gen personal AI trainers</td>
                      <td className="py-4 px-4">Teaches AI to recognize you, adapt to you</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Week of smartwatch + sleep data</td>
                      <td className="py-4 px-4">Preventative health & longevity models</td>
                      <td className="py-4 px-4">Helps train early detection systems for heart rate, burnout, etc.</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Long-form journaling & pattern mapping</td>
                      <td className="py-4 px-4">Reflective AI, mental health copilots</td>
                      <td className="py-4 px-4">Enables personalized coaching or therapy models</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">Usage across apps, media, environment</td>
                      <td className="py-4 px-4">Human digital twin frameworks</td>
                      <td className="py-4 px-4">Fuels AI that learns your preferences across platforms</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4">Mixed-signal training corpus</td>
                      <td className="py-4 px-4">General-purpose models (like GPT, Claude)</td>
                      <td className="py-4 px-4">Helps create future foundation models grounded in real lives</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-lg mt-4">
                🌍 1 GB of signal = a full week of living. That's enough to train tomorrow's most powerful models.
              </p>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">🧬 TL;DR — Signal Grows in Value as It Grows in Depth</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-4 px-4">Signal Size</th>
                      <th className="text-left py-4 px-4">Description</th>
                      <th className="text-left py-4 px-4">Impact Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">KB</td>
                      <td className="py-4 px-4">Tiny moments of presence</td>
                      <td className="py-4 px-4">Small insights, fast tweaks</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4">MB</td>
                      <td className="py-4 px-4">Patterns of behavior + emotion</td>
                      <td className="py-4 px-4">Better understanding, useful reactions</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4">GB</td>
                      <td className="py-4 px-4">Deep, full-life representations</td>
                      <td className="py-4 px-4">Personalized AI, powerful generalization</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-lg mt-4">
                Every byte of signal is a seed.<br />
                Some feed smarter products. Some grow entirely new AI.
              </p>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">🟣 Powered by Essence</h3>
              <p className="text-lg">
                Every signal unit you generate earns Essence, the first currency that reflects your presence.
              </p>
              
              <div className="mt-6">
                <p className="text-lg mb-4">
                  Want to see what your signal is worth?
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300"
                >
                  Try the Signal Calculator →
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* How the Signal Economy Works Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🔄 How the Signal Economy Works
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg">
              Every time you generate signal, you're creating value.
            </p>
            
            <p className="text-lg">
              But where does the money actually come from?
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">Here's the loop:</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2 text-xl">1.</span>
                  <div>
                    <p className="font-medium">AI companies buy access to high-quality, human signal</p>
                    <p className="text-gray-400">– They pay in real money (USD, stablecoins like USDC)</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2 text-xl">2.</span>
                  <div>
                    <p className="font-medium">That payment goes into a secure vault on the blockchain</p>
                    <p className="text-gray-400">– It holds the real-world value of that signal</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2 text-xl">3.</span>
                  <div>
                    <p className="font-medium">We mint Essence, a stable, signal-backed token</p>
                    <p className="text-gray-400">– Think of it like a prepaid card: every token is backed by AI funds</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2 text-xl">4.</span>
                  <div>
                    <p className="font-medium">You earn Essence by living your life and generating signal</p>
                    <p className="text-gray-400">– Walking, focusing, listening, speaking — being present</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">You choose how to use it:</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                  <div className="flex items-center mb-2">
                    <span className="text-indigo-400 mr-2">✅</span>
                    <span className="font-medium">Spend it on real products and services</span>
                  </div>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                  <div className="flex items-center mb-2">
                    <span className="text-indigo-400 mr-2">💸</span>
                    <span className="font-medium">Swap it for fiat or stablecoins</span>
                  </div>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                  <div className="flex items-center mb-2">
                    <span className="text-indigo-400 mr-2">🌱</span>
                    <span className="font-medium">Hold or stake it for more rewards</span>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-lg">
              Essence is not magic.<br />
              It's money — backed by the value your signal already created.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">💡 Think of It Like This:</h3>
              <p className="text-lg">
                When an AI company pays $100 for a dataset…
              </p>
              
              <div className="space-y-4 mt-4">
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>That $100 gets locked on-chain to back Essence</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>You, the creator of that dataset, earn Essence</span>
                </div>
                <div className="flex items-start">
                  <span className="text-indigo-400 mr-2">•</span>
                  <span>You can spend or convert that Essence however you choose</span>
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <p className="text-lg font-medium">Everything is transparent.</p>
                <p className="text-lg font-medium">Everything is opt-in.</p>
                <p className="text-lg font-medium">Everything starts with you.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Why Essence > Dollars Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            ✅ Why Essence &gt; Dollars
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <div className="flex items-center mb-2">
                  <span className="text-indigo-400 mr-2">🧬</span>
                  <span className="font-medium">Pegged to signal, not speculation</span>
                </div>
              </div>
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <div className="flex items-center mb-2">
                  <span className="text-indigo-400 mr-2">🔄</span>
                  <span className="font-medium">Stable and backed by real-world value</span>
                </div>
              </div>
              <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
                <div className="flex items-center mb-2">
                  <span className="text-indigo-400 mr-2">💱</span>
                  <span className="font-medium">Exchangeable for fiat — but more powerful inside the Signal Economy</span>
                </div>
              </div>
            </div>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-xl font-semibold text-indigo-400">
                Essence doesn't replace dollars.<br />
                It replaces dependency — and builds a parallel economy powered by presence.
              </p>
            </div>
          </div>
        </motion.div>

        {/* What's Your Signal Worth Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            💰 What's Your Signal Worth?
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4">Signal Type</th>
                  <th className="text-left py-4 px-4">Value to AI (Est.)</th>
                  <th className="text-left py-4 px-4">Example Use Case</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Focus (30 min)</td>
                  <td className="py-4 px-4">$0.25 – $1.00</td>
                  <td className="py-4 px-4">Trains productivity agents, tutors</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Movement + GPS (daily)</td>
                  <td className="py-4 px-4">$0.10 – $0.50</td>
                  <td className="py-4 px-4">Trains rideshare + health tracking AIs</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Mood Check-Ins</td>
                  <td className="py-4 px-4">$0.20 – $0.80</td>
                  <td className="py-4 px-4">Trains emotional AI + recommendation bots</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Voice Patterns</td>
                  <td className="py-4 px-4">$0.30 – $1.00</td>
                  <td className="py-4 px-4">Trains assistants & empathy algorithms</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Habits & Routines</td>
                  <td className="py-4 px-4">$0.50 – $1.50</td>
                  <td className="py-4 px-4">Trains personal AI coaches + reminders</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">Multi-signal Composite</td>
                  <td className="py-4 px-4">$2 – $8 / day</td>
                  <td className="py-4 px-4">Trains next-gen holistic intelligence</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
            <p className="text-lg">
              Most users generate <span className="text-indigo-400 font-semibold">$20–$100+ of value every month</span> — just by being alive.<br />
              Essence captures and returns it to you.
            </p>
          </div>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            ✅ Why Essence &gt; Dollars
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
              <div className="flex items-center mb-2">
                <span className="text-indigo-400 mr-2">🧬</span>
                <span className="font-medium">Pegged to signal units, not speculation</span>
              </div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
              <div className="flex items-center mb-2">
                <span className="text-indigo-400 mr-2">🔄</span>
                <span className="font-medium">Stable in value, based on real-world utility</span>
              </div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 border border-indigo-500/30">
              <div className="flex items-center mb-2">
                <span className="text-indigo-400 mr-2">💱</span>
                <span className="font-medium">Exchangeable for fiat, but unlocks more inside the Signal Economy</span>
              </div>
            </div>
          </div>
          
          <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
            <p className="text-lg">
              Essence doesn't replace cash.<br />
              It builds a parallel economy powered by presence.
            </p>
          </div>
        </motion.div>

        {/* What Your Signal Teaches AI Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🌍 What Your Signal Teaches AI
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4">Signal</th>
                  <th className="text-left py-4 px-4">What It Teaches</th>
                  <th className="text-left py-4 px-4">Used In</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Movement</td>
                  <td className="py-4 px-4">Mobility, rhythm, fatigue</td>
                  <td className="py-4 px-4">Health wearables, driverless systems</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Mood</td>
                  <td className="py-4 px-4">Emotional nuance, reaction types</td>
                  <td className="py-4 px-4">Music & wellness AI</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Focus</td>
                  <td className="py-4 px-4">Attention span, mental flow</td>
                  <td className="py-4 px-4">Educational apps, workflow systems</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 px-4">Voice & Speech</td>
                  <td className="py-4 px-4">Tone, intent, empathy signals</td>
                  <td className="py-4 px-4">Assistants, companionship models</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">Habits & Routines</td>
                  <td className="py-4 px-4">Timing, priority, intention</td>
                  <td className="py-4 px-4">Life coaching tools, personal AIs</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-xl text-gray-300">
              You are the input.<br />
              Essence is the reward.
            </p>
          </div>
        </motion.div>

        {/* Here's What You Already Pay For Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold mb-4 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            📦 Here's What You Already Pay For...
          </h2>
          <p className="text-xl text-center text-gray-400 mb-8">
            ...and how your signal already earned it.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {realWorldExamples.map((example) => (
              <motion.div
                key={example.id}
                whileHover={{ scale: 1.02 }}
                className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10"
              >
                <div className="flex items-center mb-4">
                  <div className="text-indigo-400 mr-3">{example.icon}</div>
                  <h3 className="text-lg font-semibold">{example.name}</h3>
                </div>
                <p className="text-gray-400 text-sm mb-2">{example.description}</p>
                <p className="text-indigo-400 text-sm font-medium mb-4">{example.signalValue}</p>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center mb-2">
                    <span className="text-indigo-400 mr-2">🧠</span>
                    <span className="text-sm font-medium">Why It Works:</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {example.id === 'rideshare' && "Your steps trained mobility AI. Now they move you."}
                    {example.id === 'music' && "Your feelings taught the algorithm. Now your songs play free."}
                    {example.id === 'food' && "Your energy powered a nutrition model. Now it powers your table."}
                    {example.id === 'phone' && "Your focus trained digital assistants. Now it covers your call."}
                    {example.id === 'streaming' && "Your reactions trained recommendation engines. Now they pay you back."}
                    {example.id === 'meditation' && "Your peace taught AI to help others. It just returned the favor."}
                    {example.id === 'gym' && "Your movement patterns trained health models. Now you work out on Essence."}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-xl text-gray-300 mb-4">
              You already paid.<br />
              Not with dollars — but with life energy.
            </p>
            <p className="text-xl text-indigo-400 font-medium">
              Essence just gives it back.
            </p>
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300"
            >
              View All Essence-Powered Services &gt;
            </motion.button>
          </div>
        </motion.div>

        {/* TL;DR Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-16 mb-12 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            💡 TL;DR – How the Signal Economy Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <span className="text-indigo-400 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-indigo-400">Live normally</h3>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <span className="text-indigo-400 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-indigo-400">Generate signal</h3>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <span className="text-indigo-400 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-indigo-400">Earn Essence</h3>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <span className="text-indigo-400 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-indigo-400">Spend or save it</h3>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-black/30 rounded-lg p-6 border border-indigo-500/30 max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold text-indigo-400 mb-3">💡 Think of it like this:</h3>
            <p className="text-lg">
              If dollars are earned by working a job…<br />
              <span className="text-indigo-400 font-semibold">Essence is earned by living your life.</span>
            </p>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-xl text-gray-300">
              "Signal is sunlight. You're the sun. Essence is the harvest."<br />
              "Signal is your heartbeat. Essence is what that rhythm earns."<br />
              "Signal is data with soul. Essence is its reward."
            </p>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-16"
        >
          <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🛍️ Ready to See What You've Already Earned?
          </h2>
          
          <div className="mt-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300"
            >
              View All Essence-Powered Services →
            </motion.button>
          </div>
        </motion.div>

        {/* AI Companies CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-24 mb-16 bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10"
        >
          <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
            🤖 For AI Companies & Builders
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-xl text-center">
              Train your models on real signal — with consent built in.
            </p>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <p className="text-lg">
                The next generation of AI can't rely on scraped data and shadow profiling.<br />
                It needs intentional signal from conscious users.
              </p>
            </div>
            
            <h3 className="text-xl font-semibold text-indigo-400 mb-4">SELF Labs gives AI companies access to:</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start">
                <span className="text-indigo-400 mr-2 text-xl">💠</span>
                <span className="text-lg">Structured, permission-based signal datasets</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-400 mr-2 text-xl">🧠</span>
                <span className="text-lg">Multi-modal data: movement, focus, speech, mood, routines</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-400 mr-2 text-xl">📊</span>
                <span className="text-lg">Signal-backed economics for fine-tuning cost efficiency</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-400 mr-2 text-xl">🛡️</span>
                <span className="text-lg">Ethically sourced training sets — zero exploitation, full traceability</span>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-indigo-400 mb-4">Why Work With Us?</h3>
            
            <div className="overflow-x-auto mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4">Problem</th>
                    <th className="text-left py-4 px-4">SELF Labs Solution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">Scraped data risks (privacy lawsuits, GDPR, etc.)</td>
                    <td className="py-4 px-4">
                      <span className="text-indigo-400">✅</span> Fully opt-in, user-controlled signal
                    </td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">Low-quality datasets</td>
                    <td className="py-4 px-4">
                      <span className="text-indigo-400">✅</span> Clean, labeled, real-world human signal
                    </td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">Model bias & brittleness</td>
                    <td className="py-4 px-4">
                      <span className="text-indigo-400">✅</span> Diverse, global, cross-modal human experiences
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">No incentive loop for contributors</td>
                    <td className="py-4 px-4">
                      <span className="text-indigo-400">✅</span> Signal Economy rewards the source: the human
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <h3 className="text-xl font-semibold text-indigo-400 mb-4">What You Get</h3>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Access to high-integrity signal via the Essence API</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Licensing options for specific signal types or verticals</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Integration with your training pipeline (LLMs, recommendation engines, wellness models, etc.)</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-2">•</span>
                <span>Custom signal portals for closed-loop collection (e.g. healthcare, finance, creative tools)</span>
              </li>
            </ul>
            
            <div className="bg-black/30 rounded-lg p-6 border border-indigo-500/30 my-6">
              <h3 className="text-xl font-semibold text-indigo-400 mb-4">🔁 Want to Partner?</h3>
              <p className="text-lg mb-4">
                Train better models.<br />
                Respect the humans behind them.<br />
                Power the Signal Economy.
              </p>
              
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all duration-300"
                >
                  Become a Signal Economy Partner →
                </motion.button>
              </div>
              
              <p className="mt-4 text-gray-400">
                or email us directly at <span className="text-indigo-400">partners@selflabs.ai</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 